import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getSignatories, addSignatory } from "services/company";
import Input from "components/Input";

const FilingAuthorizationSection = () => {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ firstName: "", lastName: "", email: "", title: "" });
    const [formError, setFormError] = useState("");
    const queryClient = useQueryClient();

    const { data, isLoading, isError } = useQuery({
        queryKey: ["signatories"],
        queryFn: getSignatories,
    });

    const signatories = Array.isArray(data) ? data : (data?.data ?? []);

    const mutation = useMutation({
        mutationFn: addSignatory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["signatories"] });
            setForm({ firstName: "", lastName: "", email: "", title: "" });
            setShowForm(false);
            setFormError("");
        },
        onError: (err) => {
            setFormError(err?.response?.data?.message || "Failed to add signatory. Please try again.");
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.firstName || !form.lastName || !form.email) {
            setFormError("First name, last name, and email are required.");
            return;
        }
        setFormError("");
        mutation.mutate(form);
    };

    const handleCancel = () => {
        setForm({ firstName: "", lastName: "", email: "", title: "" });
        setFormError("");
        setShowForm(false);
    };

    if (isLoading) return <p className="text-center mt-4">Loading filing authorization...</p>;
    if (isError) return <p className="text-danger mt-4">Failed to load signatory data.</p>;

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="sub-title">Filing Authorization</h2>
                {!showForm && (
                    <button className="button-orange py-1 px-3 rounded-pill" style={{ fontSize: 13 }} onClick={() => setShowForm(true)}>
                        + Add Signatory
                    </button>
                )}
            </div>

            <p className="title mb-4">
                Filing authorization designates individuals authorized to sign and submit tax filings on behalf of your company. At least one authorized signatory is required for payroll tax filings.
            </p>

            {showForm && (
                <div className="p-3 rounded-3 mb-4" style={{ border: "1px solid rgba(232,119,34,0.3)", backgroundColor: "rgba(232,119,34,0.05)" }}>
                    <p className="info-title mb-3">Add Authorized Signatory</p>
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-lg-6 col-12">
                                <Input label="First Name *" fullWidth variant="flat" name="firstName" value={form.firstName} onChange={handleChange} />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="Last Name *" fullWidth variant="flat" name="lastName" value={form.lastName} onChange={handleChange} />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="Email *" fullWidth variant="flat" type="email" name="email" value={form.email} onChange={handleChange} />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="Title / Role" fullWidth variant="flat" name="title" value={form.title} onChange={handleChange} placeholder="e.g. CEO, Owner, CFO" />
                            </div>
                        </div>
                        {formError && <p className="text-danger mt-2 mb-0" style={{ fontSize: 13 }}>{formError}</p>}
                        <div className="d-flex gap-2 mt-3">
                            <button type="submit" className="button-orange py-2 px-4 rounded-pill" disabled={mutation.isPending}>
                                {mutation.isPending ? "Saving..." : "Save Signatory"}
                            </button>
                            <button type="button" className="btn btn-outline-secondary rounded-pill py-2 px-4" onClick={handleCancel} disabled={mutation.isPending}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {signatories.length === 0 ? (
                <div className="text-center py-4">
                    <p className="sub-titles mb-1">No authorized signatories added</p>
                    <p className="title">Add at least one signatory to authorize tax filings.</p>
                </div>
            ) : (
                <div>
                    <p className="info-title mb-3">Authorized Signatories ({signatories.length})</p>
                    <div className="row g-3">
                        {signatories.map((s) => (
                            <div key={s.id} className="col-12">
                                <div className="p-3 rounded-3 d-flex align-items-center justify-content-between" style={{ border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "rgba(255,255,255,0.03)" }}>
                                    <div className="d-flex align-items-center gap-3">
                                        <div style={{
                                            width: 40, height: 40, borderRadius: "50%",
                                            backgroundColor: "rgba(232,119,34,0.15)",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            color: "#e87722", fontWeight: 700, fontSize: 14,
                                        }}>
                                            {(s.firstName?.[0] || "")}{(s.lastName?.[0] || "")}
                                        </div>
                                        <div>
                                            <p className="m-0 sub-titles" style={{ fontWeight: 600 }}>
                                                {s.firstName} {s.lastName}
                                            </p>
                                            <p className="m-0 title">{s.email}</p>
                                        </div>
                                    </div>
                                    <div className="text-end">
                                        {s.title && <p className="m-0 title">{s.title}</p>}
                                        <span style={{
                                            fontSize: 11, padding: "2px 8px", borderRadius: 10,
                                            backgroundColor: "rgba(34,197,94,0.15)", color: "#22c55e"
                                        }}>
                                            Authorized
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilingAuthorizationSection;
